import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ArchiveRestore, FileSpreadsheet, FileText, Loader2, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { BinExpiry } from './DeletedAnnexures';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

const BinRow = ({ it, busy, onRestore, onPurge }) => (
  <li className="flex items-center gap-2 flex-wrap py-1.5 text-xs" data-testid={`rfp-bin-item-${it.kind}-${it.id}`}>
    {it.kind === 'annexure' ? <FileSpreadsheet className="w-3.5 h-3.5 text-slate-400 shrink-0" /> : <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />}
    <span className="font-semibold text-slate-700 line-through decoration-slate-300" data-testid={`rfp-bin-item-title-${it.kind}-${it.id}`}>{it.title}</span>
    <span className="text-[10px] text-slate-500">{it.kind === 'annexure' ? 'annexure' : `section in ${it.deliverable_title}`} · {it.detail}</span>
    <span className="text-[10px] text-slate-400 ml-auto">deleted by {(it.deleted?.by || '').split('@')[0]} · {when(it.deleted?.at)}{it.deleted?.reminded_at ? ' · reminder sent' : ''}</span>
    <BinExpiry item={it} testId={`rfp-bin-item-expiry-${it.kind}-${it.id}`} />
    <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={!!busy} onClick={() => onRestore(it)} data-testid={`rfp-bin-restore-${it.kind}-${it.id}`}>
      {busy === it.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ArchiveRestore className="w-3 h-3 mr-1" />}Restore
    </Button>
    <button type="button" className="text-[10px] text-slate-400 hover:text-rose-600" disabled={!!busy} onClick={() => onPurge(it)} data-testid={`rfp-bin-purge-${it.kind}-${it.id}`}>Delete for good</button>
  </li>
);

/** Every deleted annexure and narrative section across the RFP, in one place, with restore links. */
export const ProjectBin = ({ project, onRefresh }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => axios.get(`${API_URL}/api/rfp/response/projects/${project.id}/bin`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ items: [], count: 0, soon: 0 })), [project.id]);
  useEffect(() => { load(); }, [load, project.deleted_documents?.length, project.deliverables]);
  const url = (it, suffix) => (it.kind === 'annexure'
    ? `${API_URL}/api/rfp/response/projects/${project.id}/annexures/${it.id}${suffix}`
    : `${API_URL}/api/rfp/response/projects/${project.id}/deliverables/${it.deliverable_id}/sections/${it.id}${suffix}`);
  const run = async (it, fn, ok) => {
    setBusy(it.id);
    try { await fn(); toast.success(ok); await load(); onRefresh?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not update the bin'); }
    finally { setBusy(''); }
  };
  const restore = (it) => run(it, () => axios.post(url(it, '/restore'), {}, { withCredentials: true }), `“${it.title}” is back where it was`);
  const purge = (it) => {
    if (!window.confirm(`Permanently delete “${it.title}” (${it.detail})? This cannot be undone.`)) return;
    run(it, () => axios.delete(url(it, '/permanent'), { withCredentials: true }), `“${it.title}” permanently deleted`);
  };
  return (
    <div className="border border-dashed border-slate-300 rounded-lg bg-slate-50/60 p-3" data-testid="rfp-bin">
      <div className="flex items-center gap-2 flex-wrap">
        <Trash2 className="w-4 h-4 text-slate-500" />
        <span className="text-sm font-semibold text-[#0F2040]">Bin</span>
        <span className="text-[10px] text-slate-500" data-testid="rfp-bin-count">{data ? (data.count ? `${data.count} deleted item${data.count === 1 ? '' : 's'} across this RFP — annexures and narrative sections, kept 30 days` : 'Nothing in the bin — deleted annexures and narrative sections appear here for 30 days') : 'Loading…'}</span>
        {data?.soon > 0 && <span className="inline-flex items-center gap-1 text-[10px] text-amber-800" data-testid="rfp-bin-soon"><AlertTriangle className="w-3 h-3" />{data.soon} clear{data.soon === 1 ? 's' : ''} within a week — whoever deleted them gets an email the day before</span>}
      </div>
      {data?.items?.length > 0 && (
        <ul className="mt-2 divide-y divide-slate-200" data-testid="rfp-bin-list">
          {data.items.map((it) => <BinRow key={`${it.kind}-${it.id}`} it={it} busy={busy} onRestore={restore} onPurge={purge} />)}
        </ul>
      )}
    </div>
  );
};
